import type { RouteRecordRaw } from 'vue-router';
import authSSOLoginRoutes from '@/modules/auth-ssologin/routes';
import authRoutes from '@/modules/auth/routes';
import clientReportingRoutes from '@/modules/client-reporting/routes';
import clientNetworkRoutes from '@/modules/client-network/routes';
import clientOnboardingChecklistRoutes from '@/modules/plg-client-onboarding-checklist/routes';
import profileSharingPublicRoutes from '@/modules/profile-sharing-public/routes';
import businessApprovalPublicRoutes from '@/modules/business-approval-public/routes';
import profileSharingLinksRoutes from '@/modules/profile-sharing-links/routes';
import clientDashboardRoutes from '@/modules/client-dashboard/routes';
import settingsRoutes from '@/modules/settings/routes';
import { discussionsClientRoutes, discussionsSupplierRoutes } from '@/modules/discussions/routes';
import {
  actionCentreClientRoutes,
  actionCentreSupplierRoutes,
} from '@/modules/action-centre/routes';
import connectionViewSuppliersRoutes from '@/modules/connection-view-suppliers/routes';
import connectionViewClientsRoutes from '@/modules/connection-view-clients/routes';
import { zeusRoutes } from '@/zeusIntegration';
import emergingThreatsSuppliersRoutes from '@/modules/emerging-threats-suppliers/routes';
import emergingThreatsClientsRoutes from '@/modules/emerging-threats-clients/routes';
import { risksRoutes } from '@/modules/risks/routes';
import federatedDashboardRoutes from '@/modules/federated-dashboard/routes';
import connectionViewSubEntitiesRoutes from '@/modules/connection-view-sub-entities/routes';
import federatedNetworkRoutes from '@/modules/federated-network/routes';
import federatedSubEntitiesProfileRoutes from '@/modules/federated-sub-entities-profile/routes';
import federatedSuppliersRoutes from '@/modules/federated-suppliers/routes';
import documentsRoutes from '@/modules/documents/routes';
import communitiesRoutes from '@/modules/communities/routes';
import { supplierMonitoringRoutes } from '@/modules/external-monitoring/routes';
import securityProfileSettingsRoutes from '@/modules/security-profile-settings/routes';

import { requireAuth } from '@/routes/guards/auth';
import { requireMode } from '@/routes/guards/mode';
import {
  requireClient,
  requireClientNotRestricted,
  requireFederated,
  requireSupplier,
} from '@/routes/guards/orgtype';

import clientRoutes from './clients';
import assessmentRoutes from './assessment';
import supplierRoutes from './suppliers';
import noTypeRoutes from './noType';
import logoutRoutes from './logout';
import publicRoutes from './public';

export default [
  ...authRoutes,
  ...authSSOLoginRoutes,
  ...noTypeRoutes,
  ...logoutRoutes,
  ...profileSharingPublicRoutes,
  ...businessApprovalPublicRoutes,
  ...publicRoutes,
  {
    path: '/',
    component: () => import('@/components/layouts/AppSidebarLayout.vue'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        // This beforeEnter handles the redirect when / is accessed
        beforeEnter: requireMode,
        // This component isn't rendered, it's just here to make the redirect work
        component: () => import('@/components/layouts/AppSidebarLayout.vue'),
      },
      ...settingsRoutes,
    ],
  },
  {
    path: '/c',
    beforeEnter: [requireAuth, requireClient, requireClientNotRestricted],
    component: () => import('@/components/layouts/AppSidebarLayout.vue'),
    meta: { mode: 'client' },
    // Will be further redirected to client-dashboard if not PLG trial user
    redirect: {
      name: 'plg-client-onboarding-checklist',
    },
    children: [
      ...clientDashboardRoutes,
      ...clientReportingRoutes,
      ...clientNetworkRoutes,
      ...discussionsClientRoutes,
      ...actionCentreClientRoutes,
      ...connectionViewSuppliersRoutes,
      ...clientRoutes,
      ...emergingThreatsClientsRoutes,
      ...risksRoutes,
      ...communitiesRoutes,
      ...clientOnboardingChecklistRoutes,
    ],
  },
  {
    path: '/s',
    beforeEnter: [requireAuth, requireSupplier],
    component: () => import('@/components/layouts/AppSidebarLayout.vue'),
    meta: { mode: 'supplier' },
    redirect: { name: 'supplier-getting-started' },
    children: [
      ...discussionsSupplierRoutes,
      ...actionCentreSupplierRoutes,
      ...profileSharingLinksRoutes,
      ...connectionViewClientsRoutes,
      ...assessmentRoutes,
      ...supplierRoutes,
      ...emergingThreatsSuppliersRoutes,
      ...documentsRoutes,
      ...supplierMonitoringRoutes,
      ...securityProfileSettingsRoutes,
    ],
  },
  {
    path: '/f',
    beforeEnter: [requireAuth, requireFederated],
    component: () => import('@/components/layouts/AppSidebarLayout.vue'),
    meta: { mode: 'federated' },
    redirect: { name: 'federated-dashboard' },
    children: [
      ...federatedDashboardRoutes,
      ...connectionViewSubEntitiesRoutes,
      ...federatedNetworkRoutes,
      ...federatedSubEntitiesProfileRoutes,
      ...federatedSuppliersRoutes,
    ],
  },
  // Empty in customer builds (stubbed barrel), so /zeus falls through to the
  // catchAll NotFound route below.
  ...zeusRoutes,
  {
    path: '/down-for-maintenance',
    name: 'down-for-maintenance',
    component: () => import('@/modules/errors/DownForMaintenance.vue'),
  },
  {
    path: '/:catchAll(.*)',
    component: () => import('@/modules/errors/NotFound.vue'),
  },
] satisfies RouteRecordRaw[];
